export type WorkflowWaitType =
  | 'FIXED_DURATION'
  | 'UNTIL_DATETIME'
  | 'UNTIL_CONDITION'
  | 'BUSINESS_HOURS'
  | 'PROPERTY_BASED'
  | 'EXIT_CONDITION';

export type WorkflowWaitUnit =
  | 'MINUTES'
  | 'HOURS'
  | 'DAYS'
  | 'WEEKS'
  | 'MONTHS';

export interface WorkflowWaitScheduleRequest {
  waitNodeId: number;
  context?: Record<string, any>;
}

export interface WorkflowWaitScheduleResponse {
  enrollmentId: number;
  executionRunId?: number;
  waitNodeId: number;
  queueItemId: number;
  waitType: WorkflowWaitType;
  status: string;
  availableAt: string;
  message?: string;
}

export interface WorkflowWaitResumeRequest {
  branchKey?: string;
}

export interface WorkflowWaitResumeResponse {
  enrollmentId: number;
  queueItemId?: number;
  status?: string;
  branchKey?: string;
  nextNodeId?: number;
  nextNodeType?: string;
  message?: string;
}

export interface WorkflowWaitPreviewRequest {
  waitType: WorkflowWaitType;
  config: Record<string, any>;
  context?: Record<string, any>;
}

export interface WorkflowWaitPreviewResponse {
  waitType: WorkflowWaitType;
  availableAt?: string;
  humanReadable?: string;
  message?: string;
}

/**
 * Espejo EXACTO de `WorkflowWaitQueueResponse` del backend (`GET /api/workflow-waits/queue`).
 *
 * INC_CRM_020 (6) inc3 — esta interfaz describía otra cosa: copiaba los campos de la entidad
 * `workflow_queue_item` (`id`, `idWorkflow`, `idEnrollment`, `idNode`, `payload`,
 * `datetimeCreated`…) en lugar de los del DTO que el endpoint devuelve de verdad
 * (`queueItemId`, `workflowId`, `enrollmentId`, `waitNodeId`…). Ninguno de esos nombres llega
 * nunca en la respuesta, así que en cuanto la cola tuviera una sola fila la tabla la habría
 * pintado entera vacía y la selección —que va por `item.id`— habría trabajado con `undefined`.
 * Es el mismo desfase BE/FE que en inc13 (etiquetas) e inc12 (plantillas) de la ronda (4).
 *
 * El endpoint NO devuelve `payload`: la carga del evento vive en la Cola de Eventos, no en la
 * de Esperas.
 */
export interface WorkflowWaitQueueResponse {
  queueItemId: number;
  workflowId?: number | null;
  /** INC_CRM_020 (6) inc3 — nombre del workflow; el id queda como dato secundario. */
  workflowName?: string | null;
  workflowVersionId?: number | null;
  enrollmentId?: number | null;
  waitNodeId?: number | null;
  queueType: string;
  status: string;
  objectType?: string | null;
  /** Etiqueta en español del tipo de objeto («Contacto»). */
  objectTypeLabel?: string | null;
  objectId?: number | null;
  /** Nombre del registro inscrito. Null si ya no existe: se cae al id. */
  objectName?: string | null;
  scheduledAt: string;
  availableAt: string;
  processedAt?: string | null;
  attempts: number;
  maxAttempts: number;
  lastError?: string | null;
  /** true = PENDING con availableAt vencido; false = activa (no vence aún) o estado != PENDING. */
  overdue?: boolean;
}

export interface WorkflowWaitCancelRequest {
  reason?: string;
}

export const WEEKDAY_OPTIONS = [
  'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'
] as const;

export const WEEKDAY_LABELS: Record<string, string> = {
  MONDAY: 'Lun', TUESDAY: 'Mar', WEDNESDAY: 'Mié',
  THURSDAY: 'Jue', FRIDAY: 'Vie', SATURDAY: 'Sáb', SUNDAY: 'Dom'
};
